import { useState } from "react";
import { Search, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@packages/hooks/useAnalytics";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { TextLinear, Text } from "@core/text";
import {
  deleteLessonPlannerV2,
  getGetLessonPlannersV2QueryKey,
  useGetLessonPlannersV2,
  useUpdateLessonPlannerV2,
} from "@packages/api/madlenAPI";
import { GetLessonPlannersV2PlanType } from "@packages/api/madlenAPI.schemas";
import { PastLessonPlanCard } from "../PastLessonPlanCard";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";

const NewPastMaarifDailyPlans = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const { trackEvent: track } = trackEvent();

  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { data: lessonPlans, isLoading } = useGetLessonPlannersV2({
    planType: GetLessonPlannersV2PlanType.MAARIF,
  });

  const { mutateAsync: updateLessonPlanner } = useUpdateLessonPlannerV2({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetLessonPlannersV2QueryKey({
            planType: GetLessonPlannersV2PlanType.MAARIF,
          }),
        });
      },
      onError: () => {
        addToast({
          title: t("lessonPlanner.titleUpdateError"),
          description: t("lessonPlanner.titleUpdateErrorDescription"),
          variant: "error",
        });
      },
    },
  });
  const handleDelete = async (planId: number) => {
    track(
      {
        category: "ContentDelete",
        action: "maarif.daily.planner.past.plans.delete",
        label: "MaarifDailyPlannerPastPlans delete click",
        nonInteraction: false,
      },
      {
        location: "/maarif-daily-planner-past-plans/delete",
        element: "delete-button",
      },
    );
    await deleteLessonPlannerV2(planId);
    queryClient.invalidateQueries({
      queryKey: getGetLessonPlannersV2QueryKey({
        planType: GetLessonPlannersV2PlanType.MAARIF,
      }),
    });
  };

  const handleViewDetails = (planId: number) => {
    track(
      {
        category: "ContentViewDetail",
        action: "maarif.daily.planner.past.plans.view.detail",
        label: "MaarifDailyPlannerPastPlans view details click",
        nonInteraction: false,
      },
      {
        location: "/maarif-daily-planner-past-plans/view-detail",
        element: "view-detail-button",
      },
    );
    navigate(`/lesson-planning-tools/maarif-daily-plan/${planId}`);
  };

  const handleClickGenerate = () => {
    track(
      {
        category: "ContentGeneration",
        action: "maarif.daily.planner.past.plans.generate",
        label: "MaarifDailyPlannerPastPlans generation click",
        nonInteraction: false,
      },
      {
        location: "/maarif-daily-planner-past-plans/generate",
        element: "generate-button",
      },
    );
    navigate("/lesson-planning-tools/maarif-daily-plan/new");
  };

  const handleClickFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    track(
      {
        category: "ContentFilter",
        action: "maarif.daily.planner.past.plans.filter",
        label: "MaarifDailyPlannerPastPlans filter click",
        nonInteraction: false,
      },
      {
        location: "/maarif-daily-planner-past-plans/filter",
        element: "filter-button",
      },
    );
    setSelectedCourse(e.target.value);
  };

  const courseTypes = Object.keys(CourseData);

  const filteredPlans = lessonPlans?.data?.filter((plan) => {
    const matchesSearch = searchTerm
      ? plan.name?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const matchesCourse = selectedCourse
      ? plan.courseType === selectedCourse
      : true;

    return matchesSearch && matchesCourse;
  });

  const handleUpdateTitle = async (planId: number, newTitle: string) => {
    await updateLessonPlanner({
      id: planId,
      data: {
        name: newTitle,
      },
    });
  };

  const showEmptyState =
    !isLoading && (!filteredPlans || filteredPlans.length === 0);

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <Text variant="h1" size="3xl" className="font-bold">
              {t("dailyLessonPlan.maarifTitle")}
            </Text>
            <Text
              variant="p"
              status="secondary"
              size="base"
              className="mt-2 max-w-2xl"
            >
              {t("dailyLessonPlan.maarifDescription")}
            </Text>
          </div>
          <button
            onClick={handleClickGenerate}
            className="inline-flex items-center bg-red-600 hover:bg-rose-700 text-white px-6 py-3 rounded-xl shadow-lg font-medium text-base transition-all duration-200"
          >
            + {t("dailyLessonPlan.createNew")}
          </button>
        </div>

        {/* <div className="mb-6 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-4 justify-between flex-col md:flex-row">
            <select
              value={selectedCourse}
              onChange={handleClickFilter}
              className="px-5 py-3.5 border rounded-xl text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 w-full md:max-w-xs focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 cursor-pointer hover:border-red-300 dark:hover:border-red-600"
            >
              <option value="">
                {t("dailyLessonPlan.filters.allCourses")}
              </option>
              {courseTypes.map((courseType) => (
                <option key={courseType} value={courseType}>
                  {t(`courseType.${courseType}`)}
                </option>
              ))}
            </select>

            <div className="relative flex flex-1 w-full md:max-w-md">
              <input
                type="text"
                placeholder={t("dailyLessonPlan.search.placeholder")}
                className="pl-12 pr-4 py-3.5 border rounded-xl w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 hover:border-red-300 dark:hover:border-red-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                size={20}
                className="absolute left-4 top-4 text-gray-400 dark:text-gray-500"
              />
            </div>
          </div>
        </div> */}

        {!showEmptyState && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredPlans?.map((plan) => (
              <PastLessonPlanCard
                key={plan.id}
                plan={plan}
                onViewDetails={handleViewDetails}
                onDelete={() => setShowDeletePopup(plan.id)}
                colorTheme="maarif"
                onUpdateTitle={handleUpdateTitle}
              />
            ))}
          </div>
        )}

        {showEmptyState && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8 bg-white dark:bg-dark-600 rounded-2xl shadow-sm p-16 border border-gray-100 dark:border-gray-700">
            <div className="p-8 rounded-full bg-red-50 dark:bg-red-900/30 ring-8 ring-red-50/50 dark:ring-red-900/20">
              <Package size={64} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="text-center space-y-4 max-w-lg">
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {searchTerm || selectedCourse
                  ? t("dailyLessonPlan.noResults.title")
                  : t("dailyLessonPlan.empty.title")}
              </h3>
              <Text variant="p" status="subtle" size="lg">
                {searchTerm || selectedCourse
                  ? t("dailyLessonPlan.noResults.description")
                  : t("dailyLessonPlan.empty.description")}
              </Text>
            </div>
            <button
              onClick={handleClickGenerate}
              className="inline-flex items-center bg-red-600 hover:bg-rose-700 text-white px-8 py-4 rounded-xl shadow-lg font-medium text-lg transition-all duration-200"
            >
              + {t("dailyLessonPlan.empty.createButton")}
            </button>
          </div>
        )}

        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("dailyPlanner.confirmDelete", {
              title: filteredPlans?.find((plan) => plan.id === showDeletePopup)
                ?.name,
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={() => handleDelete(showDeletePopup)}
          />
        )}
      </div>
    </div>
  );
};

export default NewPastMaarifDailyPlans;
